import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
  AxiosError,
} from "axios";
import { authStoreApi } from "../store/authStore";

const API_BASE_URL = (import.meta.env.VITE_API_URL || "") + "/api/v1";

/**
 * Instancia axios con:
 *  - Bearer automatico desde authStore.
 *  - Interceptor de refresh automatico en 401 (una sola vez por request).
 *  - withCredentials=true para enviar la cookie HttpOnly refreshToken.
 */
export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

// --- Request interceptor: anade Authorization Bearer ---
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = authStoreApi.getAccessToken();
  if (token) {
    config.headers.set("Authorization", `Bearer ${token}`);
  }
  return config;
});

// --- Response interceptor: refresca en 401 y reintenta UNA vez ---

type RetryConfig = AxiosRequestConfig & { _retry?: boolean; _noRefresh?: boolean };

let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      const r = await axios.post<{ accessToken: string }>(
        `${API_BASE_URL}/auth/refresh`,
        null,
        { withCredentials: true },
      );
      const newToken = r.data.accessToken;
      authStoreApi.setAccessToken(newToken);
      return newToken;
    } catch {
      authStoreApi.clear();
      return null;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

api.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const original = error.config as RetryConfig | undefined;
    const status = (error as AxiosError).response?.status;

    // Solo intentamos refresh en 401, una unica vez por request,
    // y no para la propia ruta de refresh.
    if (
      status === 401 &&
      original &&
      !original._retry &&
      !original._noRefresh &&
      !(original.url ?? "").includes("/auth/refresh") &&
      !(original.url ?? "").includes("/auth/login")
    ) {
      original._retry = true;
      const newToken = await refreshAccessToken();
      if (newToken) {
        if (!original.headers) original.headers = new axios.AxiosHeaders();
        if (original.headers instanceof axios.AxiosHeaders) {
          original.headers.set("Authorization", `Bearer ${newToken}`);
        } else {
          (original.headers as Record<string, string>).Authorization =
            `Bearer ${newToken}`;
        }
        return api.request(original);
      }
    }
    return Promise.reject(error);
  },
);

/**
 * Helper para hidratar la sesion al cargar la app:
 * intenta obtener un accessToken via /auth/refresh (si hay cookie).
 * Devuelve true si la sesion esta activa.
 */
export async function tryHydrateSession(): Promise<boolean> {
  const token = await refreshAccessToken();
  return token !== null;
}
